import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { VerifyEmailRequest } from '../../dto/verify-email-request';
import { RegisterUserRequest } from '../../dto/register-user-request';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RegistrationResponse } from '../../dto/registration-response.enum';
import { NavigationExtras, Router } from '@angular/router';
import { FormGroup, FormControl, Validators, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SecurityService } from '../../services/security.service';
import { AppStateService, RECAPTCHA_SITE_KEY, RecaptchaDirective } from '@pcplus/core';
import { HeaderItemLocation, passwordMatchValidator } from '@pcplus/infrastructure';
import { NgIf } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatCard, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatStepper, MatStep } from '@angular/material/stepper';
import { MatFormField, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'pcp-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    imports: [NgIf, FlexModule, MatCard, MatToolbar, MatIcon, MatCardContent, FormsModule, ReactiveFormsModule, MatStepper, MatStep, MatFormField, MatInput, MatError, MatButton, MatProgressSpinner, MatSuffix, MatCardActions, RecaptchaDirective]
})
export class RegisterComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(RECAPTCHA_SITE_KEY) public recaptchaSiteKey: string,
    private security: SecurityService,
    private snackBar: MatSnackBar,
    private router: Router,
    private state: AppStateService
  ) {}
  verifyingEmail = false;
  registering = false;
  error = '';
  registeredSuccessfully = false;
  @Input() standAlone = true;
  @Output() loginRequested = new EventEmitter();
  loggingIn = false;
  headerItemLocation = HeaderItemLocation;
  private onDestroy$ = new Subject<void>();
  verifyRequest = new VerifyEmailRequest('', '', '', '');
  registerRequest = new RegisterUserRequest('', '', '', null, '', '', '');
  registerForm = new FormGroup(
    {
      verifyGroup: new FormGroup({
        firstName: new FormControl('', Validators.required),
        lastName: new FormControl('', Validators.required),
        email: new FormControl('', [Validators.required, Validators.email]),
        captcha: new FormControl(null, Validators.required)
      }),
      code: new FormControl<number>(0, Validators.required),
      phoneNumber: new FormControl(''),
      organizationName: new FormControl(''),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(6)
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(6)
      ])
    },
    passwordMatchValidator
  );

  get verifyGroup() {
    return this.registerForm.get('verifyGroup');
  }
  get firstName() {
    const verify = this.verifyGroup;
    if (verify) {
      return verify.get('firstName');
    } else {
      return null;
    }
  }
  get lastName() {
    const verify = this.verifyGroup;
    if (verify) {
      return verify.get('lastName');
    } else {
      return null;
    }
  }
  get email() {
    const verify = this.verifyGroup;
    if (verify) {
      return verify.get('email');
    } else {
      return null;
    }
  }
  get captcha() {
    const verify = this.verifyGroup;
    if (verify) {
      return verify.get('captcha');
    } else {
      return null;
    }
  }
  get code() {
    return this.registerForm.get('code');
  }
  get password() {
    return this.registerForm.get('password');
  }
  get confirmPassword() {
    return this.registerForm.get('confirmPassword');
  }
  ngOnInit() {
    this.state.updateCurrentModuleName($localize`Registration`);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  verifyEmail() {
    this.verifyingEmail = true;
    this.verifyRequest.email = this.email ? this.email.value : null;
    this.verifyRequest.firstName = this.firstName ? this.firstName.value : null;
    this.verifyRequest.lastName = this.lastName ? this.lastName.value : null;
    this.verifyRequest.captcha = this.captcha ? this.captcha.value : null;
    this.security
      .verifyEmail(this.verifyRequest)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result === RegistrationResponse.Sent) {
          if (this.captcha) {
            this.captcha.setErrors(null);
          }
          this.snackBar.open(
            $localize`Email Sent! Please check your inbox.`,
            $localize`OK`,
            {
              duration: 5000
            }
          );
        } else {
          switch (result) {
            case RegistrationResponse.EmailAlreadyExists:
              if (this.email) {
                this.email.setErrors({ emailAlreadyExists: true });
              }
              break;
            case RegistrationResponse.FirstNameRequired:
              this.errorFirstNameRequired();
              break;
            case RegistrationResponse.InvalidCaptcha:
              if (this.captcha) {
                this.captcha.setErrors({ invalidCaptcha: true });
              }
              break;
            case RegistrationResponse.InvalidEmail:
              this.errorInvalidEmail();
              break;
            case RegistrationResponse.LastNameRequired:
              this.errorLastNameRequired();
              break;
            case RegistrationResponse.Unknown:
              if (this.verifyGroup) {
                this.verifyGroup.setErrors({ unknown: true });
              }
              break;
          }
        }
        this.verifyingEmail = false;
      });
  }

  private errorInvalidEmail() {
    if (this.email) {
      this.email.setErrors({ invalidEmail: true });
    }
  }

  // Should not reach here, since we already have the required validator.
  private errorFirstNameRequired() {
    if (this.firstName) {
      this.firstName.setErrors({ firstNameRequired: true });
    }
  }

  // Should not reach here, since we already have the required validator.
  private errorLastNameRequired() {
    if (this.lastName) {
      this.lastName.setErrors({ lastNameRequired: true });
    }
  }

  register() {
    this.registering = true;
    this.registerRequest.firstName = this.firstName
      ? this.firstName.value
      : null;
    this.registerRequest.lastName = this.lastName ? this.lastName.value : null;
    this.registerRequest.email = this.email ? this.email.value : null;
    this.registerRequest.code = this.code ? this.code.value : null;
    if (this.registerForm) {
      const orgName = this.registerForm.get('organizationName');
      if (orgName) {
        this.registerRequest.organizationName = orgName.value;
      }
      const phoneNum = this.registerForm.get('phoneNumber');
      if (phoneNum) {
        this.registerRequest.phoneNumber = phoneNum.value;
      }
    }
    this.registerRequest.password = this.password ? this.password.value : null;
    this.security
      .registerUser(this.registerRequest)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result === RegistrationResponse.Success) {
          this.registeredSuccessfully = true;
          setTimeout(() => {
            const navigationExtras: NavigationExtras = {
              queryParamsHandling: 'preserve',
              preserveFragment: true
            };
            this.router.navigate(['/login'], navigationExtras);
          }, 5000);
        } else {
          switch (result) {
            case RegistrationResponse.FirstNameRequired:
              this.errorFirstNameRequired();
              break;
            case RegistrationResponse.InvalidCode:
              if (this.code) {
                this.code.setErrors({ invalidCode: true });
              }
              break;
            case RegistrationResponse.InvalidEmail:
              this.errorInvalidEmail();
              break;
            case RegistrationResponse.LastNameRequired:
              this.errorLastNameRequired();
              break;
            case RegistrationResponse.PasswordTooShort:
              if (this.password) {
                this.password.setErrors({ passwordTooShort: true });
              }
              break;
            case RegistrationResponse.Unknown:
              this.registerForm.setErrors({ unknown: true });
              break;
          }
          this.registering = false;
        }
      });
  }
}
